import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { queryPatternService } from "../services/queryPatternService";
import { queryPatternDisplayId } from "./QueryPatternCommonStore";
import { QueryPattern } from "../../../api/graphql";
import { EvaluationPathType } from "../../../api/graphql";
import { QueryPatternSource } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class QueryPatternViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = queryPatternService;
  queryPatternInstance: QueryPattern | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";
  patternValue: string = "";
  pathTypeValue: EvaluationPathType = "" as EvaluationPathType;
  variablesValue: string = "{}";
  toolTemplateValue: string = "";
  argsTemplateValue: string = "{}";
  variationsValue: string = "{}";
  exampleInstantiationValue: string = "";
  sourceValue: QueryPatternSource = "" as QueryPatternSource;
  qualityScoreValue: number = 0;
  usageCountValue: number = 0;
  isActiveValue: boolean = false;
  notesValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadQueryPatternById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.queryPatternOne({ id });
    this.queryPatternInstance = result.queryPatternOne;
    if (this.queryPatternInstance) {
      this.loadValues(this.queryPatternInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.queryPatternInstance) {
      return queryPatternDisplayId(this.queryPatternInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.QueryPatternSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getPathTypeOptions() {
    return this.langStore.EvaluationPathTypeOptions;
  }

  getSourceOptions() {
    return this.langStore.QueryPatternSourceOptions;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.queryPatternInstance = null;
    this.nameValue = "";
    this.patternValue = "";
    this.pathTypeValue = "" as EvaluationPathType;
    this.variablesValue = "{}";
    this.toolTemplateValue = "";
    this.argsTemplateValue = "{}";
    this.variationsValue = "{}";
    this.exampleInstantiationValue = "";
    this.sourceValue = "" as QueryPatternSource;
    this.qualityScoreValue = 0;
    this.usageCountValue = 0;
    this.isActiveValue = false;
    this.notesValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelQueryPattern() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("queryPattern_update");
  }

  editQueryPattern() {
    const id = this.queryPatternInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/queryPattern/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printQueryPattern(handlePrint: () => void): void {
    handlePrint();
  }

  *closeQueryPattern(): any {
    yield this.close();
  }

  loadValues(instance: QueryPattern) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
    this.patternValue = instance.pattern;
    this.pathTypeValue = instance.pathType;
    this.variablesValue = instance.variables;
    this.toolTemplateValue = instance.toolTemplate;
    this.argsTemplateValue = instance.argsTemplate;
    this.variationsValue = instance.variations;
    this.exampleInstantiationValue = instance.exampleInstantiation;
    this.sourceValue = instance.source;
    this.qualityScoreValue = instance.qualityScore;
    this.usageCountValue = instance.usageCount;
    this.isActiveValue = instance.isActive;
    this.notesValue = instance.notes || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadQueryPatternById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
